import React from 'react'
import { useConfigStore } from '@renderer/stores/configState'
import { getColorBrightness } from '@renderer/utils/colorUtils'

/**
 * Keep the operator's chosen text color when it reads against `bg`; otherwise
 * fall back to black/white so a low-contrast theme (e.g. primaryColor and
 * secondaryColor both light) never renders an invisible label.
 */
const readableLabel = (bg: string, preferred: string): string => {
  const bgBrightness = getColorBrightness(bg)
  if (Math.abs(bgBrightness - getColorBrightness(preferred)) >= 60) return preferred
  return bgBrightness < 128 ? '#FFFFFF' : '#000000'
}

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  text: React.ReactNode
  variant?: 'primary' | 'secondary'
  fullWidth?: boolean
}

const Button: React.FC<ButtonProps> = ({
  text,
  variant = 'primary',
  fullWidth = false,
  className = '',
  style,
  ...props
}) => {
  const { config } = useConfigStore()
  const { primaryColor, secondaryColor } = config?.theme || {
    primaryColor: '#3B82F6',
    secondaryColor: '#FFFFFF'
  }

  const baseClasses = `
    rounded-full
    font-medium
    transition-all
    duration-500
    select-none
    active:scale-95
    hover:opacity-90
    focus:outline-none
    focus:ring-2
    focus:ring-offset-2
    disabled:opacity-50
    disabled:cursor-not-allowed
    px-[clamp(1.5rem,3vw,3rem)]
    py-[clamp(0.75rem,1.5vw,1rem)]
    text-[clamp(1.2rem,2vw,1.5rem)]
    ${fullWidth ? 'w-full' : ''}
  `

  const bg = variant === 'primary' ? primaryColor : secondaryColor
  const preferredLabel = variant === 'primary' ? secondaryColor : primaryColor
  const labelColor = readableLabel(bg, preferredLabel)

  const buttonStyle = {
    ...style,
    backgroundColor: bg,
    color: labelColor,
    border: variant === 'secondary' ? `1px solid ${primaryColor}` : 'none'
  }
  return (
    <button
      type="button"
      className={`${baseClasses} ${className} transition-transform transform shadow-xl active:scale-95`}
      style={buttonStyle}
      {...props}
    >
      {text}
    </button>
  )
}

export default Button
